/** “Why guests choose us” — copy + imagery for profile-style cards on the home page. Photos: `public/quality/1.jpg`–`5.jpg`; optional background videos `11.mp4` (hygiene), `12.mp4` (premium products). */

export type WhyGuestPillar = {
  id: string
  title: string
  /** Short line under the title (profile-card “handle” style) */
  subtitle: string
  text: string
  /** Still used as `<img>` source, or as the `poster` when `videoSrc` is set */
  imageSrc: string
  imageAlt: string
  /** Optional looping background clip (muted). `imageSrc` is used as `poster`. */
  videoSrc?: string
  /** Tailwind gradient classes — light wash overlaid on the full-bleed header photo */
  headerGradient: string
}

export const whyGuestsPillars = [
  {
    id: 'hygiene',
    title: 'Hygiene first',
    subtitle: 'Our non-negotiable',
    text: 'Sanitized tools, single-use where it matters, and stations kept guest-ready.',
    imageSrc: '/quality/1.jpg',
    imageAlt: 'Short looping clip: clean, bright nail studio — hygiene focused',
    videoSrc: '/quality/11.mp4',
    headerGradient: 'from-[#e8cfc9] via-[#dcb8b0] to-[#c9a399]',
  },
  {
    id: 'products',
    title: 'Premium products',
    subtitle: 'Chosen for wear',
    text: 'Polishes and care products chosen for finish, longevity, and nail health.',
    imageSrc: '/quality/2.jpg',
    imageAlt: 'Short looping clip: polish and product styling — premium products',
    videoSrc: '/quality/12.mp4',
    headerGradient: 'from-[#ddd4e4] via-[#cfc0d6] to-[#b9a8c4]',
  },
  {
    id: 'atmosphere',
    title: 'Calm atmosphere',
    subtitle: 'Room to exhale',
    text: 'Soft lighting, unhurried pacing, and a space designed to help you unwind.',
    imageSrc: '/quality/3.jpg',
    imageAlt: 'Calm salon atmosphere — quality photography',
    headerGradient: 'from-[#d8e0d4] via-[#cad4c5] to-[#b5c2ae]',
  },
  {
    id: 'detail',
    title: 'Detail obsessed',
    subtitle: 'Up-close quality',
    text: 'Cuticle work, symmetry, and art that looks intentional when you look closer.',
    imageSrc: '/quality/4.jpg',
    imageAlt: 'Nail detail and finish work — quality photography',
    headerGradient: 'from-[#e8dcc8] via-[#dccfb8] to-[#c9b89a]',
  },
  {
    id: 'booking',
    title: 'Modern booking',
    subtitle: 'On your schedule',
    text: 'Pick a time online whenever you like; we will have your spot ready when you arrive.',
    imageSrc: '/quality/5.jpg',
    imageAlt: 'Illustration of manicured hands holding a phone — book online anytime',
    headerGradient: 'from-[#e5d2cf] via-[#d4bab5] to-[#c4a399]',
  },
] as const satisfies readonly WhyGuestPillar[]
